import { createAsyncThunk } from '@reduxjs/toolkit'

import questionsRequest from 'src/api/requests/questions'
import questionsActions from 'src/store/domain/automaticPlan/questions'
import menuActions from 'src/store/domain/automaticPlan/menus'
import history from 'src/libs/history'
import uiActions from 'src/store/ui/notification'
import backFlowActions from 'src/store/domain/automaticPlan/backFlow'
import { PATH as AUTOMATIC_PLAN_PATH } from 'src/pages/automaticPlan'
import { UserInterface } from 'src/interfaces/user'
import { defaultUserInfo } from './../interfaces/user.d'
import { get } from 'lodash'
import LocalStorage from 'src/utils/LocalStorage'
import { PATH } from 'src/pages/video/CompleteTemporaryReserve'
import { USER_TREATMENT_STATE } from 'src/enum/userTreatmentState'
import { PATH as PATH_CHAT } from 'src/pages/chat/Chat'
import { PATH as PATH_REGULAR } from 'src/pages/regular'

export interface QuestionRequestInterface {
  menu_id: string
}

export interface MenuRequestType {
  selected?: boolean
  allow_menu_ids?: Array<string | number>
}

export const getMenus = createAsyncThunk<any, MenuRequestType>(
  'get_menus',
  async (params, { dispatch }) => {
    try {
      dispatch(menuActions.updateLoading(true))

      const res = await questionsRequest.getMenus(params)
      if (res.data) {
        dispatch(menuActions.updateEntity(res.data.data))
      }
      dispatch(menuActions.updateLoading(false))
    } catch (error) {
      dispatch(menuActions.updateLoading(false))
    }
  },
)

export const getMenusByLp = createAsyncThunk(
  'automaticPlan/getMenusByLp',
  async (
    { clinicId, params }: { clinicId: string; params?: Record<string, any> },
    { dispatch },
  ) => {
    try {
      dispatch(menuActions.updateLoading(true))
      const res = await questionsRequest.getMenusByLp(clinicId, params || {})
      if (res.data) {
        dispatch(menuActions.updateEntity(res.data.data))
      }
      return res.data
    } finally {
      dispatch(menuActions.updateLoading(false))
    }
  },
)

export const getQuestionsByMenuId = createAsyncThunk<
  any,
  QuestionRequestInterface
>('get_questions_by_menu_id', async ({ menu_id }, { dispatch }) => {
  try {
    dispatch(questionsActions.updateLoading(true))
    dispatch(questionsActions.updateEntity([]))
    const res = await questionsRequest.getQuestionsByMenuId(menu_id)
    if (res.data) {
      dispatch(questionsActions.updateEntity(res.data))
    }
    dispatch(questionsActions.updateLoading(false))
  } catch (error) {
    dispatch(questionsActions.updateLoading(false))
  }
})

export const getQuestions = createAsyncThunk<any, QuestionRequestInterface>(
  'get_questions',
  async ({ menu_id }, { dispatch }) => {
    try {
      dispatch(questionsActions.updateLoading(true))
      const res = await questionsRequest.getQuestions(menu_id)
      if (res.data) {
        dispatch(questionsActions.updateEntity(res.data))
      }
      dispatch(questionsActions.updateLoading(false))
    } catch (error) {
      dispatch(questionsActions.updateLoading(false))
    }
  },
)

export const getMenuById = createAsyncThunk(
  'automaticPlan/getMenuById',
  async (
    {
      menuId,
      params,
    }: {
      menuId: string | number
      params?: Record<string, any>
    },
    { dispatch, getState },
  ) => {
    try {
      dispatch(menuActions.updateLoading(true))
      const state = getState() as any
      const user = get(state, 'ui.app.authInfo')
      const queryParams = params ? { ...params } : {}

      if (!queryParams.clinic_id && user?.clinics?.[0]?.id) {
        queryParams.clinic_id = user.clinics[0].id
      }

      const res = await questionsRequest.getMenuById(menuId, queryParams)
      const data = get(res, 'data.data')

      if (data) {
        const cleanedData = {
          ...data,
          plans: data.plans || [],
        }
        dispatch(menuActions.updateEntity([cleanedData]))
      }

      return data
    } finally {
      dispatch(menuActions.updateLoading(false))
    }
  },
)

export interface PostAnswer {
  answer: any
  question_id: number
  question_type: string
}
export interface PostAnswersInterView {
  answers: PostAnswer[]
  reservation_id: string
  user_treatment_id?: string | number
  menu_id?: string | number
  from?: string
  params?: any
}

export const answerInterView = createAsyncThunk<any, PostAnswersInterView>(
  'answer_interview',
  async (
    { answers, reservation_id, user_treatment_id, menu_id, from, params },
    { dispatch, getState },
  ) => {
    try {
      dispatch(questionsActions.updateLoading(true))
      const res = await questionsRequest.answer({
        answers,
        reservation_id,
        user_treatment_id,
      })
      const state = getState()
      const user: UserInterface = get(state, 'ui.app.authInfo', defaultUserInfo)
      const isTelemedicine = Boolean(user?.clinics?.[0]?.telemedicine_enabled)
      const userTreatmentState = res?.data?.user_treatment.state

      if (!user?.otp_verified_at) {
        dispatch(
          backFlowActions.setBackFlow({
            from: from ?? null,
            params: params ?? null,
          }),
        )
        return history.push(
          `${AUTOMATIC_PLAN_PATH.SMS_AUTH}?menu_id=${menu_id}`,
        )
      } else if (isTelemedicine) {
        history.push(PATH)
        LocalStorage.removeTelemedicine()
      } else {
        switch (userTreatmentState) {
          case USER_TREATMENT_STATE.AWAITING_PLAN:
            return history.push(
              `${AUTOMATIC_PLAN_PATH.FINAL_QUESTIONS.replace(
                ':id',
                String(menu_id || ''),
              )}`,
            )

          case USER_TREATMENT_STATE.CHAT_INTERVIEW_SCHEDULED:
          case USER_TREATMENT_STATE.CHAT_CONSULTATION_IN_PROGRESS:
            return history.push(PATH_CHAT)

          default:
            history.push(
              `${PATH_REGULAR.REGULAR_SUBSCRIPTION}?menu_id=${menu_id}`,
            )
            break
        }
      }

      if (res.data) {
        dispatch(questionsActions.updateEntity(res.data?.questions))
      }
      dispatch(questionsActions.updateLoading(false))
    } catch (error: any) {
      const message = error?.response?.data.message
      dispatch(questionsActions.updateLoading(false))
      dispatch(
        uiActions.onNotification({
          type: 'error',
          message: message,
        }),
      )
    }
  },
)

export const draffAnswer = createAsyncThunk<any, PostAnswersInterView>(
  'draff_answer',
  async ({ answers, reservation_id }, { dispatch }) => {
    try {
      dispatch(questionsActions.updateDraffLoading(true))
      await questionsRequest.draffAnswer({
        answers,
        reservation_id,
      })
      dispatch(questionsActions.updateDraffLoading(false))
    } catch (error) {
      dispatch(questionsActions.updateDraffLoading(false))
    }
  },
)
